import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import "./DonationPage.css";

// Stripe-supported countries (name and ISO alpha-2 code)
const stripeCountries = [
  { name: "Australia", code: "AU" },
  { name: "Austria", code: "AT" },
  { name: "Belgium", code: "BE" },
  { name: "Brazil", code: "BR" },
  { name: "Bulgaria", code: "BG" },
  { name: "Canada", code: "CA" },
  { name: "Croatia", code: "HR" },
  { name: "Cyprus", code: "CY" },
  { name: "Czech Republic", code: "CZ" },
  { name: "Denmark", code: "DK" },
  { name: "Estonia", code: "EE" },
  { name: "Finland", code: "FI" },
  { name: "France", code: "FR" },
  { name: "Germany", code: "DE" },
  { name: "Gibraltar", code: "GI" },
  { name: "Greece", code: "GR" },
  { name: "Hong Kong", code: "HK" },
  { name: "Hungary", code: "HU" },
  { name: "India", code: "IN" },
  { name: "Ireland", code: "IE" },
  { name: "Italy", code: "IT" },
  { name: "Japan", code: "JP" },
  { name: "Latvia", code: "LV" },
  { name: "Liechtenstein", code: "LI" },
  { name: "Lithuania", code: "LT" },
  { name: "Luxembourg", code: "LU" },
  { name: "Malaysia", code: "MY" },
  { name: "Malta", code: "MT" },
  { name: "Mexico", code: "MX" },
  { name: "Netherlands", code: "NL" },
  { name: "New Zealand", code: "NZ" },
  { name: "Norway", code: "NO" },
  { name: "Poland", code: "PL" },
  { name: "Portugal", code: "PT" },
  { name: "Romania", code: "RO" },
  { name: "Singapore", code: "SG" },
  { name: "Slovakia", code: "SK" },
  { name: "Slovenia", code: "SI" },
  { name: "Spain", code: "ES" },
  { name: "Sweden", code: "SE" },
  { name: "Switzerland", code: "CH" },
  { name: "Thailand", code: "TH" },
  { name: "United Arab Emirates", code: "AE" },
  { name: "United Kingdom", code: "GB" },
  { name: "United States", code: "US" },
  { name: "Vietnam", code: "VN" },
];

// US states
const usStates = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

// Canadian provinces
const caProvinces = [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Nova Scotia",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
];

const BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://127.0.0.1:8000"
    : "https://bonobohope-chazselph.pythonanywhere.com";

const getStripePromise = async () => {
  try {
    const response = await fetch(`${BASE_URL}/api/stripe-publishable-key/`);
    const data = await response.json();
    if (!response.ok) throw new Error("Failed to fetch Stripe key");
    console.log("Stripe publishable key fetched:", data.publishableKey);
    return loadStripe(data.publishableKey);
  } catch (err) {
    console.error("Error fetching Stripe publishable key:", err);
    throw err;
  }
};

const stripePromise = getStripePromise();

const DonationForm = () => {
  const [amount, setAmount] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [billingFirstName, setBillingFirstName] = useState("");
  const [billingLastName, setBillingLastName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [regionType, setRegionType] = useState("none");
  const [postalCode, setPostalCode] = useState("");
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  // Update region field based on country
  useEffect(() => {
    if (countryCode === "US") {
      setRegionType("state");
      setRegion("Alabama");
    } else if (countryCode === "CA") {
      setRegionType("province");
      setRegion("Alberta");
    } else if (countryCode) {
      setRegionType("region");
      setRegion("");
    } else {
      setRegionType("none");
      setRegion("");
    }
  }, [countryCode]);

  useEffect(() => {
    if (!stripe || !elements) {
      console.log("Stripe or Elements not yet loaded");
    } else {
      console.log("Stripe and Elements loaded successfully");
    }
  }, [stripe, elements]);

  const validateFields = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const postalRegex = /^[a-zA-Z0-9\s-]{3,10}$/;
    const amountNum = parseFloat(amount);

    if (!firstName || firstName.length < 2)
      return "First Name must be at least 2 characters.";
    if (!lastName || lastName.length < 2)
      return "Last Name must be at least 2 characters.";
    if (!email || !emailRegex.test(email)) return "Invalid email address.";
    if (!amount || isNaN(amountNum) || amountNum <= 0)
      return "Amount must be a positive number.";
    if (!billingFirstName || billingFirstName.length < 2)
      return "Billing First Name must be at least 2 characters.";
    if (!billingLastName || billingLastName.length < 2)
      return "Billing Last Name must be at least 2 characters.";

    // Optional address validation
    if (addressLine1 && addressLine1.length < 3)
      return "Address Line 1 must be at least 3 characters if provided.";
    if (city && city.length < 2)
      return "City must be at least 2 characters if provided.";
    if (countryCode === "US" && !region)
      return "State is required for United States.";
    if (countryCode === "CA" && !region)
      return "Province is required for Canada.";
    if (postalCode && !postalRegex.test(postalCode))
      return "Invalid postal code (e.g., 12345, A1B 2C3, SW1A 1AA).";

    if (!countryCode) {
      console.warn(
        "Country not provided; proceeding with payment but fraud risk may increase."
      );
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log("Form submitted");

    if (!stripe || !elements) {
      setError("Payment system not initialized. Please wait and try again.");
      console.log("Stripe or Elements not ready");
      return;
    }

    const validationResult = validateFields();
    if (validationResult !== true) {
      setError(validationResult);
      console.log("Validation failed:", validationResult);
      return;
    }

    setIsProcessing(true);
    setError(null);

    const debouncedSubmit = debounce(async () => {
      try {
        console.log("Creating payment intent");
        const response = await axios.post(
          `${BASE_URL}/api/create-payment-intent/`,
          {
            amount,
            first_name: firstName,
            last_name: lastName,
            email,
            metadata: { transaction_type: "donation" },
          }
        );

        const { clientSecret, customerId } = response.data;
        console.log(
          "Payment intent created:",
          clientSecret,
          "Customer ID:",
          customerId
        );

        const { error: stripeError, paymentIntent } =
          await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: elements.getElement(CardNumberElement),
              billing_details: {
                name: `${billingFirstName} ${billingLastName}`,
                email: email,
                ...(addressLine1 || city || region || postalCode || countryCode
                  ? {
                      address: {
                        line1: addressLine1 || "",
                        line2: addressLine2 || "",
                        city: city || "",
                        state: region || "",
                        postal_code: postalCode || "",
                        country: countryCode || "",
                      },
                    }
                  : {}),
              },
            },
          });

        if (stripeError) {
          console.log("Stripe error:", stripeError.message);
          setError(stripeError.message);
          navigate("/failure", {
            state: { error: "Payment failed. Please try again." },
          });
        } else if (paymentIntent.status === "succeeded") {
          console.log("Payment succeeded");

          const emailPayload = {
            payment_intent_id: paymentIntent.id,
            amount: amount,
            first_name: firstName,
            last_name: lastName,
            email: email,
            country: countryCode
              ? stripeCountries.find((c) => c.code === countryCode)?.name
              : "N/A",
            region: region || "N/A",
          };

          try {
            console.log("Sending donation email");
            const emailResponse = await axios.post(
              `${BASE_URL}/api/send-donation-email/`,
              emailPayload
            );
            if (!emailResponse.status === 200) {
              console.error("Email response not OK:", emailResponse.status);
              throw new Error("Failed to send donation email");
            }
            console.log("Email sent, navigating to success");
            navigate("/donation-success", { replace: true });
          } catch (emailErr) {
            console.error("Email error:", emailErr);
            navigate("/donation-email-failure", {
              state: { donationDetails: emailPayload },
            });
          }
        }
      } catch (err) {
        console.error("Error in donation submission:", err);
        setError(
          "An error occurred while processing your donation. Please try again or contact support."
        );
        navigate("/failure", { state: { error: "An error occurred" } });
      } finally {
        setIsProcessing(false);
      }
    }, 1000);

    debouncedSubmit();
  };

  return (
    <div className="donation-page">
      <div className="donation-form-container">
        <h2 className="donation-title">Donate to Bonobo Hope</h2>
        <p className="intro-text">
          We welcome your donation to support BHI’s mission.
          <p className="secure-note">
            Donations are processed using a secure SSL encrypted payment system.
          </p>
        </p>
        <form className="donation-form" onSubmit={handleSubmit}>
          <div className="form-section-block">
            {/* Personal Info Section */}
            <div className="form-section">
              <h3 className="section-title">Personal Info</h3>
              <div className="input-group">
                <label htmlFor="firstName" className="input-label">
                  First Name <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  className="donation-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="lastName" className="input-label">
                  Last Name <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  className="donation-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="email" className="input-label">
                  Email <span className="required-asterisk">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  className="donation-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="country" className="input-label">
                  Country
                </label>
                <select
                  id="country"
                  className="donation-input"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  <option value="">Select a country</option>
                  {stripeCountries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="input-group">
                <label htmlFor="addressLine1" className="input-label">
                  Address Line 1
                </label>
                <input
                  type="text"
                  id="addressLine1"
                  className="donation-input"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="Enter address"
                />
              </div>
              <div className="input-group">
                <label htmlFor="addressLine2" className="input-label">
                  Address Line 2
                </label>
                <input
                  type="text"
                  id="addressLine2"
                  className="donation-input"
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  placeholder="Apt, suite, etc. (optional)"
                />
              </div>
              <div className="input-group">
                <label htmlFor="city" className="input-label">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  className="donation-input"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Enter city"
                />
              </div>
              {regionType !== "none" && (
                <div className="input-group">
                  <label htmlFor="region" className="input-label">
                    {regionType === "state"
                      ? "State"
                      : regionType === "province"
                      ? "Province"
                      : "Region"}{" "}
                    {(countryCode === "US" || countryCode === "CA") && (
                      <span className="required-asterisk">*</span>
                    )}
                  </label>
                  {regionType === "state" ? (
                    <select
                      id="region"
                      className="donation-input"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      required={countryCode === "US"}
                    >
                      {usStates.map((stateOption) => (
                        <option key={stateOption} value={stateOption}>
                          {stateOption}
                        </option>
                      ))}
                    </select>
                  ) : regionType === "province" ? (
                    <select
                      id="region"
                      className="donation-input"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      required={countryCode === "CA"}
                    >
                      {caProvinces.map((province) => (
                        <option key={province} value={province}>
                          {province}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      id="region"
                      className="donation-input"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      placeholder="Enter region (optional)"
                    />
                  )}
                </div>
              )}
              <div className="input-group">
                <label htmlFor="postalCode" className="input-label">
                  Postal Code
                </label>
                <input
                  type="text"
                  id="postalCode"
                  className="donation-input"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="Enter postal code (e.g., 12345, A1B 2C3)"
                />
              </div>
            </div>

            {/* Billing Info Section */}
            <div className="form-section">
              <h3 className="section-title">Billing Info</h3>
              <div className="input-group">
                <label htmlFor="billingFirstName" className="input-label">
                  Billing First Name{" "}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="billingFirstName"
                  className="donation-input"
                  value={billingFirstName}
                  onChange={(e) => setBillingFirstName(e.target.value)}
                  placeholder="Enter billing first name"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="billingLastName" className="input-label">
                  Billing Last Name <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  id="billingLastName"
                  className="donation-input"
                  value={billingLastName}
                  onChange={(e) => setBillingLastName(e.target.value)}
                  placeholder="Enter billing last name"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="cardNumber" className="input-label">
                  Card Number <span className="required-asterisk">*</span>
                </label>
                <div className="card-element-wrapper">
                  <CardNumberElement id="cardNumber" className="card-element" />
                </div>
              </div>
              <div className="input-group card-details-group">
                <div className="card-detail">
                  <label htmlFor="cardExpiry" className="input-label">
                    Expiration <span className="required-asterisk">*</span>
                  </label>
                  <CardExpiryElement id="cardExpiry" className="card-element" />
                </div>
                <div className="card-detail">
                  <label htmlFor="cardCvc" className="input-label">
                    CVC <span className="required-asterisk">*</span>
                  </label>
                  <CardCvcElement id="cardCvc" className="card-element" />
                </div>
              </div>
              <div className="input-group amount-total-group">
                <div className="amount-total-field">
                  <label htmlFor="amount" className="input-label">
                    Donation Amount (USD){" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="number"
                    id="amount"
                    className="donation-input amount-input"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter amount"
                    min="1"
                    step="0.01"
                    required
                  />
                </div>
                <div className="amount-total-field">
                  <label className="input-label">Donation Total</label>
                  <div className="donation-total">
                    ${parseFloat(amount || 0).toFixed(2)}
                  </div>
                </div>
              </div>
              <button
                type="submit"
                className="donation-button"
                disabled={isProcessing || !stripe || !elements}
              >
                {isProcessing ? "Processing..." : "Donate"}
              </button>
            </div>
          </div>
        </form>
        {error && <div className="donation-error">{error}</div>}
      </div>
      {isProcessing && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
      <div className="donation-footer">
        <p>
          Bonobo Hope Initiative is an NGO registered with the US Internal
          Revenue Service as a 501(c) non-profit organization (EIN# 45-3218086).
          Your donation may be tax-deductible depending on your country’s tax
          laws.
        </p>
        <p>
          If your country isn’t listed in the dropdown, please select the
          country where your payment method is registered (e.g., United States
          or United Kingdom).
        </p>
        <p className="privacy-policy">
          Privacy policy: BHI does not record or store credit card information.
          Credit card payments are handled by Stripe. BHI tags donations with
          address information to mail donation records at the end of the year
          for tax purposes. BHI will not sell or share donor names or contact
          information with other organizations.
        </p>
      </div>
    </div>
  );
};

const DonationPage = () => (
  <Elements stripe={stripePromise}>
    <DonationForm />
  </Elements>
);

export default DonationPage;
